import {
  type Company,
  POS_SALE_STATUS_LABELS,
  POS_SALE_WARNING_LABELS,
  type PosSaleStatus,
  type PosSaleWarning,
  POS_TENDER_TYPE_LABELS,
  type PosTenderType,
} from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
  type Tone,
} from "@station/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, Fuel, MonitorOff } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { CompanyPicker, companySearchShape, useAccountingCompany } from "../accounting/company.js";
import { useCompanyStations } from "../accounting/shared.js";
import { useStationScope } from "../cashflow/shared.js";
import { usePosPermissions } from "./queries.js";

/** Kasa ekranlarının ortak arama parametreleri: şirket (defter) ve istasyon (süzgeç). */
export const posSearchShape = {
  ...companySearchShape,
  station: z.string().uuid().optional().catch(undefined),
};

export interface PosContext {
  companyId: string;
  company: Company;
}

/**
 * Yerel kasa ekranlarının ortak çerçevesi: başlık + ŞİRKET seçici (muhasebe / vardiya / ajan ekranlarıyla
 * aynı `?company=` parametresi) ve modül / izin kapıları. İstasyon burada KAPSAM değil SÜZGEÇTİR
 * (satış listesi tüm istasyonları gösterebilir), bu yüzden seçici sayfanın içinde durur.
 *
 * Breadcrumb "Cihazlar ve ajanlar › Yerel kasa › <sayfa>" biçimindedir: kenar çubuğunda grup içi alt başlık
 * kavramı olmadığı için "Yerel kasa" başlığı buraya ve etiketlere taşındı.
 */
export function PosPage({
  title,
  description,
  breadcrumb,
  actions,
  children,
}: {
  title: string;
  description?: string;
  /** "Yerel kasa" kökünden SONRAKİ öğeler; verilmezse yalnızca sayfa başlığı eklenir. */
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode | ((context: PosContext) => ReactNode);
  children: (context: PosContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  const permissions = usePosPermissions();
  useDocumentTitle(title);
  const context = accounting.company
    ? { companyId: accounting.company.id, company: accounting.company }
    : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[
          { label: t("menu.groups.devices") },
          { label: t("pos.groupLabel") },
          ...(breadcrumb ?? [{ label: title }]),
        ]}
        actions={
          <>
            <CompanyPicker accounting={accounting} />
            {context && permissions.enabled && permissions.canRead
              ? typeof actions === "function"
                ? actions(context)
                : actions
              : null}
          </>
        }
      />
      {!permissions.enabled ? (
        <ModuleDisabled />
      ) : !permissions.canRead ? (
        <Alert tone="info">{t("pos.noPermission")}</Alert>
      ) : accounting.error ? (
        <Alert tone="danger">{errorMessage(accounting.error)}</Alert>
      ) : accounting.isLoading ? (
        <SkeletonRows rows={6} />
      ) : context === null ? (
        <EmptyState
          headingLevel={2}
          icon={<Building2 />}
          title={t("accounting.company.noneTitle")}
          description={t("accounting.company.noneDescription")}
        />
      ) : (
        // Şirket değişince sayfanın yerel durumu (süzgeç, sekme, seçili kampanya) sıfırlanır.
        <div key={context.companyId} className="grid gap-4">
          {children(context)}
        </div>
      )}
    </>
  );
}

/** `LOCAL_POS` kapalı: API 403 `FEATURE_DISABLED` döndüğü için hiç istek atılmaz, boş durum çizilir. */
export function ModuleDisabled(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      headingLevel={2}
      icon={<MonitorOff />}
      title={t("pos.disabledTitle")}
      description={t("pos.disabledDescription")}
    />
  );
}

export interface PosStationOption {
  id: string;
  code: string;
  name: string;
}

export interface PosStationSelection {
  stations: PosStationOption[];
  /** Süzgeçte seçili istasyon; `null` = tümü. */
  stationId: string | null;
  /** Tek istasyonlu kiracıda ve görünür tek istasyonda seçici çizilmez (AGENTS.md §2). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string | null) => void;
}

export function usePosStations(companyId: string): PosStationSelection {
  const stations = useCompanyStations(companyId);
  const scope = useStationScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible = (stations.data ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const selected = visible.find((station) => station.id === search.station) ?? null;
  return {
    stations: visible,
    stationId: selected?.id ?? null,
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: stations.isPending,
    error: stations.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({
          ...previous,
          station: stationId ?? undefined,
        })) as never,
        replace: true,
      }),
  };
}

export function PosStationField({
  selection,
  className = "w-64",
  allowAll = true,
}: {
  selection: PosStationSelection;
  className?: string;
  /** false → "Tümü" seçeneği yoktur (kampanya / ayar kapsamı tek istasyona bakar). */
  allowAll?: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("pos.fields.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.stationId ?? ""}
          onChange={(event) => selection.select(event.target.value || null)}
        >
          {allowAll ? <option value="">{t("common.all")}</option> : null}
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

/** Hiç istasyon görünmüyorsa (kapsam ya da kurulum eksik) ekranın geri kalanı çizilmez. */
export function NoStations(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      headingLevel={2}
      icon={<Fuel />}
      title={t("pos.noStationsTitle")}
      description={t("pos.noStationsDescription")}
      action={
        <Link to="/ayarlar/istasyonlar" className="text-sm underline underline-offset-4">
          {t("menu.stations")}
        </Link>
      }
    />
  );
}

export const POS_SALE_STATUS_TONE: Record<PosSaleStatus, Tone> = {
  COMPLETED: "success",
  VOID: "danger",
};

export function PosSaleStatusBadge({ status }: { status: PosSaleStatus }): ReactElement {
  return <StatusBadge tone={POS_SALE_STATUS_TONE[status]}>{POS_SALE_STATUS_LABELS[status]}</StatusBadge>;
}

/**
 * Uyarı rozeti: anlam YALNIZCA renkle taşınmaz (WCAG 1.4.1) — rozet uyarı SAYISINI yazar, tam metinler
 * `title` ve ayrıntı sayfasındaki listede okunur. Bilinmeyen kod (yeni sunucu, eski istemci) ham hâliyle
 * gösterilir; sessizce yutulmaz.
 */
export function warningLabel(code: string): string {
  return POS_SALE_WARNING_LABELS[code as PosSaleWarning] ?? code;
}

export function PosWarningBadge({ warnings }: { warnings: readonly string[] }): ReactElement | null {
  const { t } = useTranslation();
  if (warnings.length === 0) return null;
  const labels = warnings.map(warningLabel);
  return (
    <StatusBadge tone="warning">
      <span title={labels.join(" · ")}>{t("pos.sales.warningCount", { count: warnings.length })}</span>
    </StatusBadge>
  );
}

export function tenderLabel(code: string): string {
  return POS_TENDER_TYPE_LABELS[code as PosTenderType] ?? code;
}

/** Tahsilat türü rozetleri (bir fişte birden çok tür olabilir: parçalı ödeme). */
export function TenderBadges({ types }: { types: readonly string[] }): ReactElement {
  if (types.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {types.map((type) => (
        // Rozet içinde satır kırılmaz ("Kredi / banka kartı" hap içinde üç satıra bölünüyordu); birden çok
        // rozet `flex-wrap` ile alt satıra geçer.
        <StatusBadge key={type} tone="neutral" className="whitespace-nowrap">
          {tenderLabel(type)}
        </StatusBadge>
      ))}
    </span>
  );
}

/** Tanım listesi satırı (`<dl>` içinde kullanılır). */
export function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}
